import React, { useMemo, useState, useEffect } from "react";
import { Label, Input, Col, Row, Card, CardBody, Badge, InputGroup } from "reactstrap";

const OverviewCard = ({
  formik,
  getTotalPayments,
  handleVATChange,
  totalPaidPercentage,
  getTotalSettlementPrice,
}) => {
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [tempPrice, setTempPrice] = useState(formik.values.settled_price || 0);

  useEffect(() => {
    if (!isEditingPrice) {
      setTempPrice(formik.values.settled_price || 0);
    }
  }, [formik.values.settled_price, isEditingPrice]);

  const totalPrice = useMemo(
    () => parseInt(formik.values.settled_price) || 0,
    [formik.values.settled_price]
  );
  const totalPayments = useMemo(() => getTotalPayments(), [getTotalPayments]);
  const remainingAmount = useMemo(
    () => totalPrice - totalPayments,
    [totalPrice, totalPayments]
  );
  const unitCount = useMemo(
    () => formik.values.units.length,
    [formik.values.units]
  );
  const currentVATPercentage = useMemo(
    () =>
      formik.values.payments.length > 0
        ? formik.values.payments[0].vat_percentage
        : "19",
    [formik.values.payments]
  );

  const formatCurrency = (amount) =>
    `€${Intl.NumberFormat("en-US").format(amount)}`;

  const handlePriceEditToggle = () => {
    if (isEditingPrice) {
      const newPrice = parseInt(tempPrice) || 0;
      
      formik.setFieldValue("settled_price", newPrice);
      formik.setFieldValue("settlement_price", newPrice);
      
      formik.setFieldValue("isManuallyEdited", true);
    } else {
      setTempPrice(formik.values.settled_price || 0);
    }
    setIsEditingPrice(!isEditingPrice);
  };

  const handlePriceChange = (e) => {
    setTempPrice(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handlePriceEditToggle();
    }
  };

  return (
    <Card
      className="mb-4 shadow-sm border-0"
      style={{
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.9), rgba(255,255,255,0.7))",
        backdropFilter: "blur(10px)",
        borderRadius: "12px",
      }}
    >
      <CardBody className="text-center py-4">
        <Row className="g-4 justify-content-center align-items-center">
          <Col md={2} className="text-center">
            <div className="bg-light rounded-pill py-2 px-3 d-inline-block mb-1">
              <span className="text-muted me-2">Units:</span>
              <Badge color="primary" pill className="px-3 py-2">
                {unitCount}
              </Badge>
            </div>
          </Col>

          <Col md={2} className="text-center">
            <div
              className="rounded-pill py-2 px-3 d-inline-block mb-1"
              style={{ background: "rgba(25, 135, 84, 0.1)", cursor: "pointer" }}
              onClick={handlePriceEditToggle}
            >
              <span className="text-muted me-2">Total:</span>
              {isEditingPrice ? (
                <InputGroup size="sm" className="d-inline-flex" style={{ width: "100px" }}>
                  <span className="input-group-text">€</span>
                  <Input
                    type="number"
                    value={tempPrice}
                    onChange={handlePriceChange}
                    onKeyDown={handleKeyDown}
                    onBlur={handlePriceEditToggle}
                    autoFocus
                    className="form-control"
                    min="0"
                  />
                </InputGroup>
              ) : (
                <span className="fw-bold text-success">
                  {formatCurrency(totalPrice)}
                </span>
              )}
            </div>
          </Col>

          <Col md={3} className="text-center">
            <div
              className="bg-danger bg-opacity-10 rounded-pill py-2 px-3 d-inline-block mb-1"
              id="remainingTooltip"
            >
              <span className="text-muted me-2">Remaining:</span>
              <span className="fw-bold text-danger">
                {formatCurrency(remainingAmount)}
              </span>
            </div>
          </Col>

          <Col md={3} sm={6} xs={12}>
            <div
              className="bg-light rounded-pill py-1 px-3 d-flex justify-content-between align-items-center mb-1"
              style={{ maxWidth: "180px" }}
            >
              <Label className="mb-0 small">VAT Rate</Label>
              <Input
                type="select"
                className="form-select form-select-sm rounded-pill"
                style={{
                  width: "65px",
                  fontSize: "0.8rem",
                  padding: "0.15rem 0.5rem",
                }}
                value={currentVATPercentage}
                onChange={(e) => handleVATChange(e.target.value)}
              >
                <option value="19">19%</option>
                <option value="9">9%</option>
                <option value="0">0%</option>
              </Input>
            </div>
          </Col>
        </Row>

        {/* Progress Bar */}
        <div className="mt-4 position-relative" style={{ height: "10px" }}>
          <div
            className="progress"
            style={{
              height: "10px",
              borderRadius: "5px",
              backgroundColor: "#f8f9fa",
            }}
          >
            <div
              className="progress-bar bg-success"
              role="progressbar"
              style={{ width: `${totalPaidPercentage}%` }}
              aria-valuenow={totalPaidPercentage}
              aria-valuemin="0"
              aria-valuemax="100"
            ></div>
          </div>
          <div className="d-flex justify-content-between mt-1">
            <small className="text-muted">€0</small>
            <small className="text-success fw-bold">
              {formatCurrency(totalPayments)} settled ({totalPaidPercentage}%)
            </small>
            <small className="text-muted">{formatCurrency(totalPrice)}</small>
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default OverviewCard;